import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { STORES_REPORT_IDS, type ProductDto } from '@gc/shared';

import { Collapsible } from '@/components/Collapsible';
import { useAuth } from '@/features/auth/useAuth';
import { StockAdjustDialog } from '@/features/cafe/StockAdjustDialog';
import { useProducts, type ProductFilters } from '@/features/cafe/useCafe';
import { ReportRangePicker } from '@/features/reports/ReportRangePicker';
import { ReportTableView } from '@/features/reports/ReportTableView';
import {
  rangeFromBusinessDay,
  useReportCatalogue,
  useReportTable,
  type ReportRangeState,
} from '@/features/reports/useReports';

/**
 * Store (SPEC §5.7 Stores reports + §6.3 stock).
 *
 * One screen for the person who owns the shelf: the four existing Stores
 * reports as sub-tabs, and above them a shortcut strip of the products
 * themselves so the follow-up to "the report says we are low" — adjusting the
 * count — happens here rather than three screens away in Cafe settings.
 *
 * Nothing on this page has its own endpoint. The reports go through the same
 * catalogue/table hooks and `ReportTableView` as the Reports screen (which
 * keeps print/CSV/PDF working identically), and the strip reads the same
 * product list and posts through the same `StockAdjustDialog` as §6.3.
 */

export type StoreReportId = (typeof STORES_REPORT_IDS)[number];

/** `stores.stockMovement` -> `stockMovement`, the store.tabs.* lookup key. */
const tabKey = (id: StoreReportId): string => id.slice(id.indexOf('.') + 1);

/** Active products only: a de-listed product has no shelf to manage. */
const STRIP_FILTERS: ProductFilters = {
  search: '',
  productCategoryId: null,
  tagId: null,
  onlyActive: true,
  lowStockOnly: false,
};

/**
 * Low-stock products first — they are why the strip exists — then by name so
 * the rest is scannable. Pure and exported for tests.
 */
export function orderForStrip(products: readonly ProductDto[]): ProductDto[] {
  return [...products].sort(
    (a, b) => Number(b.isLowStock) - Number(a.isLowStock) || a.name.localeCompare(b.name),
  );
}

export function StorePage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const canViewReports = can('reports.view');
  const canAdjust = can('products.manage');

  const { data, loading, error, reload } = useProducts(STRIP_FILTERS);
  const [adjusting, setAdjusting] = useState<ProductDto | null>(null);
  // Bumped after every stock adjustment so the open report re-fetches: three
  // of the four sub-tabs read the numbers the dialog just changed.
  const [stockVersion, setStockVersion] = useState(0);

  return (
    <div className="flex h-full flex-col overflow-auto">
      {/* Same print scoping as the Reports screen: the report table is what
          goes on paper, not the strip or the chrome. */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .gc-report-print, .gc-report-print * { visibility: visible !important; }
          .gc-report-print {
            position: absolute !important;
            inset-inline-start: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          .gc-no-print { display: none !important; }
        }
      `}</style>

      <header className="gc-no-print flex flex-wrap items-center gap-3 px-4 pt-4">
        <h1 className="text-xl font-semibold text-content">{t('store.title')}</h1>
        <p className="text-xs text-content-muted">{t('store.subtitle')}</p>
      </header>

      <section className="gc-no-print px-4 pt-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {t('store.strip.loadFailed')}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : data ? (
          <StoreProductStrip
            products={orderForStrip(data.products)}
            lowStockCount={data.lowStockCount}
            canAdjust={canAdjust}
            onAdjust={setAdjusting}
          />
        ) : null}
      </section>

      <div className="min-h-0 flex-1 px-4 pb-4 pt-5">
        {canViewReports ? (
          <StoreReports stockVersion={stockVersion} />
        ) : (
          // Visible-but-explained beats missing: the strip above still works
          // for a stock-taking employee without the Reports privilege.
          <p className="text-sm text-content-muted">{t('store.noReportsPermission')}</p>
        )}
      </div>

      {adjusting ? (
        <StockAdjustDialog
          product={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={() => {
            void reload();
            setStockVersion((version) => version + 1);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The products the reports talk about, one chip each: live count, a low-stock
 * badge, and the §6.3 adjust action. Presentational and exported so the strip
 * rules (badge on low stock, adjust disabled without the privilege) can be
 * pinned by tests without auth or fetching.
 */
export function StoreProductStrip({
  products,
  lowStockCount,
  canAdjust,
  onAdjust,
}: {
  products: readonly ProductDto[];
  lowStockCount: number;
  canAdjust: boolean;
  onAdjust: (product: ProductDto) => void;
}) {
  const { t } = useTranslation();
  const lowCount =
    lowStockCount > 0 ? t('store.strip.lowCount', { count: lowStockCount }) : undefined;

  return (
    <div>
      {/* On a phone the fold's own button carries the title and the low
          count, so this heading would only say it twice. */}
      <div
        className={`mb-2 flex flex-wrap items-baseline gap-3 ${products.length > 0 ? 'compact:hidden' : ''}`}
      >
        <h2 className="text-base font-semibold text-content">{t('store.strip.title')}</h2>
        {lowCount ? (
          <span className="rounded-full bg-ending/20 px-2 py-0.5 text-xs text-ending">
            {lowCount}
          </span>
        ) : null}
      </div>

      {products.length === 0 ? (
        <p className="text-sm text-content-muted">{t('store.strip.empty')}</p>
      ) : (
        /*
          On a phone the strip WRAPS (no sideways swiping), and wrapped, a
          menu of sixty products was every pixel above the report it serves
          (owner, 2026-09-23: "the categories overfill the whole screen and
          we cannot see the data"). So there it folds: shut by default, the
          low count on the button, capped and scrolling inside when open.
          A desktop keeps its one sideways row, untouched.
        */
        <Collapsible
          label={t('store.strip.title')}
          summary={lowCount}
          storageKey="store.strip"
          layout="block"
          cap
          panelClassName="mt-2"
        >
          <div className="flex gap-2 overflow-x-auto pb-2 compact:flex-wrap compact:overflow-x-visible">
            {products.map((product) => (
              <div
                key={product.id}
                data-low-stock={product.isLowStock ? 'true' : 'false'}
                className={`flex shrink-0 items-center gap-2 rounded-card border px-3 py-2 ${
                  product.isLowStock ? 'border-ending/60 bg-ending/10' : 'border-subtle'
                }`}
              >
                <div>
                  <div className="text-sm text-content">{product.name}</div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span
                      className={`tabular ${product.isLowStock ? 'text-ending' : 'text-content-muted'}`}
                    >
                      {t('store.strip.inStock', { count: product.stockQuantity })}
                    </span>
                    {product.isLowStock ? (
                      <span className="rounded-full bg-ending/20 px-1.5 py-0.5 font-medium text-ending">
                        {t('store.strip.low')}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onAdjust(product)}
                  disabled={!canAdjust}
                  title={canAdjust ? undefined : t('store.strip.noAdjustPermission')}
                  className="inline-flex items-center gap-1 rounded-full border border-subtle px-2.5 py-1 text-xs text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {t('store.strip.adjust')}
                </button>
              </div>
            ))}
          </div>
        </Collapsible>
      )}
    </div>
  );
}

/**
 * Sub-tab bar over the four Stores reports. Exported for tests; state lives
 * in the caller.
 */
export function StoreReportTabBar({
  active,
  onSelect,
}: {
  active: StoreReportId;
  onSelect: (id: StoreReportId) => void;
}) {
  const { t } = useTranslation();

  return (
    <nav
      // Scrollable like the bills strip: on a phone the tabs ran 9px past the
      // edge and dragged the whole column with them.
      className="gc-no-print flex gap-1 overflow-x-auto border-b border-subtle"
      aria-label={t('store.title')}
    >
      {STORES_REPORT_IDS.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          aria-current={id === active ? 'page' : undefined}
          className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
            id === active
              ? 'border-primary font-medium text-content'
              : 'border-transparent text-content-secondary hover:text-content'
          }`}
        >
          {t(`store.tabs.${tabKey(id)}`)}
        </button>
      ))}
    </nav>
  );
}

/**
 * The report pane: catalogue-seeded range, sub-tabs, and the shared
 * `ReportTableView` (which brings print and the permission-gated CSV/PDF
 * exports with it). A separate component so its hooks only run for callers
 * that hold `reports.view` — mounting them without it would fire requests
 * whose 403 is already known.
 */
function StoreReports({ stockVersion }: { stockVersion: number }) {
  const { t } = useTranslation();
  const { catalogue, error: catalogueError } = useReportCatalogue();

  const [tab, setTab] = useState<StoreReportId>(STORES_REPORT_IDS[0]);
  const [draftRange, setDraftRange] = useState<ReportRangeState | null>(null);
  const [appliedRange, setAppliedRange] = useState<ReportRangeState | null>(null);

  // Seed both ranges from the venue's own business day once it is known —
  // same convention as the Reports screen, so the two never disagree about
  // what "today" means.
  useEffect(() => {
    if (!catalogue || draftRange) return;
    const seeded = rangeFromBusinessDay(
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
    );
    setDraftRange(seeded);
    setAppliedRange(seeded);
  }, [catalogue, draftRange]);

  const { table, loading, error, reload } = useReportTable(tab, appliedRange);

  // Re-fetch when a stock adjustment lands, but not on mount — the table hook
  // already fetches then, and a doubled first request would race itself.
  const seenVersion = useRef(stockVersion);
  useEffect(() => {
    if (seenVersion.current === stockVersion) return;
    seenVersion.current = stockVersion;
    void reload();
  }, [stockVersion, reload]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <StoreReportTabBar active={tab} onSelect={setTab} />

      {draftRange ? (
        <div className="gc-no-print pt-4">
          <ReportRangePicker
            value={draftRange}
            onChange={setDraftRange}
            onFetch={() => setAppliedRange(draftRange)}
            busy={loading}
            resolved={table?.range ?? null}
          />
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col pt-4">
        {catalogueError ? (
          <p role="alert" className="text-sm text-danger">
            {catalogueError}
          </p>
        ) : error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !table ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : table && appliedRange ? (
          <ReportTableView report={table} range={appliedRange} />
        ) : (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        )}
      </div>
    </div>
  );
}
